import type { ReactNode } from 'react';

import { pluralize } from '@flintmint/core-shared/utils';
import { Modal } from '@flintmint/web-shared/components/modal';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Divider } from '@flintmint/web-shared/components/divider';

import { useSafeLogout } from '@/hooks/use-safe-logout';

import styles from './save-before-logout-modal.module.scss';

import SavingSvg from './assets/saving.svg?react';
import AiCloudSvg from './assets/ai-cloud.svg?react';

export const SaveBeforeLogoutModal = () => {
  const { syncProgress, showSyncModal, forceLogout, cancelLogout } = useSafeLogout();

  return (
    <LogoutModal open={showSyncModal} title="Give us a second" saving>
      <Loader type="progress" className={styles.savingProgress} progress={syncProgress} data-testid="progress-bar" />
      <p>{`You have changes that are saved locally,\n please wait while the syncing is done`}</p>
      <button onClick={forceLogout}>Logout & Lose Data</button>
      <button onClick={cancelLogout}>Cancel</button>
    </LogoutModal>
  );
};

type UnsyncedChangesLogoutModalProps = {
  // undefined when the count could not be read.
  unsyncedCount: number | undefined;
  // The local database could not be opened (DB6/COL12 recovery).
  localDataError: boolean;
  onLogout: () => void;
};

// Shown by the logout route before anything is deleted, for logouts that did not come through the
// waiting modal above (a revoked session, the local-data recovery). There is no cancel: the session
// or the local database is already unusable, so logging out is the only way on.
export const UnsyncedChangesLogoutModal = ({ unsyncedCount, localDataError, onLogout }: UnsyncedChangesLogoutModalProps) => {
  const changesLost = unsyncedCount !== 0;

  return (
    <LogoutModal open title={changesLost ? 'Unsynced changes' : 'Please log back in'}>
      {localDataError && <p>Your locally saved data could not be opened.</p>}
      {changesLost && (
        <p>
          {unsyncedCount === undefined
            ? 'Some changes may not have been saved to the cloud and will be lost.'
            : `${pluralize(unsyncedCount, 'change')} could not be saved to the cloud and will be lost.`}
        </p>
      )}
      <button onClick={onLogout}>{changesLost ? 'Logout & Lose Data' : 'Log out'}</button>
    </LogoutModal>
  );
};

// `saving` shows the syncing artwork — only for the modal that is actually waiting on a sync.
const LogoutModal = ({
  open,
  title,
  saving = false,
  children,
}: {
  open: boolean;
  title: string;
  saving?: boolean;
  children: ReactNode;
}) => (
  <Modal open={open} freeForm maxWidth={350} closeOnEscape={false}>
    <ul className={styles.saveBeforeLogoutModal}>
      <li className={styles.title}>{title}</li>
      <Divider as="li" orientation="horizontal" />
      <li className={styles.content}>
        <div className={styles.saving}>
          <div className={styles.cloudHolder}>
            <AiCloudSvg />
          </div>
          {saving && <SavingSvg />}
        </div>
        {children}
      </li>
    </ul>
  </Modal>
);
